import { CircleNotchIcon as Spinner, TreeStructureIcon as Tree } from "@phosphor-icons/react";
import EvidenceBadge from "@/components/metadata/EvidenceBadge";
import ConfidenceBadge from "@/components/fielddeps/ConfidenceBadge";
import TypeBadge from "@/components/fielddeps/TypeBadge";
import { originLabel } from "@/lib/fieldDependencyConstants";
import { screenMappingLabel } from "@/lib/screenFields";
import { blockedByAmbiguousFormat, inheritedViaBinding } from "@/lib/screenBindings";

const TH = "px-3 py-2 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]";

function ProgramCell({ r }) {
  if (r.program_name) {
    return (
      <span data-testid={`fd-program-${r.id}`} data-inherited={inheritedViaBinding(r) ? "binding" : "none"} title={r.usage_context}>
        {r.program_library && <span className="text-[var(--ml-text-secondary)]">{r.program_library}/</span>}{r.program_name}
        {inheritedViaBinding(r) && <span className="ml-1 ml-mono text-[9px] uppercase tracking-widest text-[#0e6027]" title="Program inherited from an observed screen format binding (EXFMT / WRITE / READ / READC)">· bound</span>}
      </span>
    );
  }
  if (blockedByAmbiguousFormat(r)) {
    return <span className="text-[#8a6d00]" data-testid={`fd-program-${r.id}`} data-inherited="ambiguous" title={r.usage_context}>ambiguous format name</span>;
  }
  return <span className="text-[var(--ml-text-secondary)]/60" data-testid={`fd-program-${r.id}`} data-inherited="none" title={r.usage_context}>—</span>;
}

function FileCell({ r }) {
  const screen = screenMappingLabel(r);
  if (screen) {
    if (screen.kind === "mapped") {
      return <span title={screen.hint} data-testid={`fd-file-${r.id}`} data-mapping="mapped">{r.file_library && <span className="text-[var(--ml-text-secondary)]">{r.file_library}/</span>}{r.file_name}<span className="text-[#0e6027]"> · REF</span></span>;
    }
    return <span className={screen.kind === "ambiguous" ? "text-[#8a6d00]" : "text-[var(--ml-text-secondary)]"} title={screen.hint} data-testid={`fd-file-${r.id}`} data-mapping={screen.kind}>{screen.text}</span>;
  }
  if (r.file_name) return <>{r.file_library && <span className="text-[var(--ml-text-secondary)]">{r.file_library}/</span>}{r.file_name}</>;
  return <span className="text-[var(--ml-text-secondary)]" title={r.usage_context}>{r.ambiguous ? "ambiguous" : "unresolved"}</span>;
}

/** Field reference inventory table (Iter 41). Click a row → detail drawer. */
export default function FieldRefTable({ page, loading, workspaceId, onSelect, selectedId, canRebuild, onRebuild }) {
  return (
    <div className="ml-card overflow-x-auto" data-testid="fd-table-wrap">
      {loading ? (
        <div className="p-12 flex items-center justify-center text-sm text-[var(--ml-text-secondary)]" data-testid="fd-loading">
          <Spinner size={18} className="animate-spin mr-2" /> Loading field references…
        </div>
      ) : !page || page.items.length === 0 ? (
        <div className="p-12 text-center" data-testid="fd-empty">
          <Tree size={36} weight="duotone" color="#0f62fe" className="mx-auto mb-3" />
          <div className="font-medium mb-1">{page?.total === 0 && page?.summary?.references === 0 ? "No field references yet" : "Nothing matches these filters"}</div>
          <p className="text-sm text-[var(--ml-text-secondary)] max-w-md mx-auto">
            {page?.summary?.references === 0
              ? "Parse RPG / DDS source, import a DSPFFD extract, then rebuild. References come only from deterministic evidence — never from DSPFFD alone."
              : "Loosen the filters or clear them to see the full inventory."}
          </p>
          {canRebuild && page?.summary?.references === 0 && (
            <button type="button" onClick={onRebuild} className="ml-btn-secondary mt-4" data-testid="fd-empty-rebuild-btn">
              Rebuild field references
            </button>
          )}
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="fd-table">
          <thead>
            <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
              <th className={TH}>Field</th>
              <th className={TH}>Program</th>
              <th className={TH}>Screen</th>
              <th className={TH}>File</th>
              <th className={TH}>Type</th>
              <th className={TH}>Origin</th>
              <th className={TH}>Confidence</th>
              <th className={TH}>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {page.items.map((r, i) => (
              <tr
                key={r.id}
                onClick={() => onSelect(r.id)}
                data-testid={`fd-row-${r.id}`}
                data-confidence={r.confidence}
                data-ambiguous={r.ambiguous}
                data-screen={r.display_file ? "true" : "false"}
                className={`border-b border-[var(--ml-border)] last:border-b-0 cursor-pointer hover:bg-[var(--ml-primary)]/5 ${
                  selectedId === r.id ? "bg-[var(--ml-primary)]/8" : i % 2 ? "bg-[var(--ml-surface)]/30" : ""
                }`}
              >
                <td className="px-3 py-2 ml-mono font-semibold whitespace-nowrap">{r.field_name}</td>
                <td className="px-3 py-2 ml-mono text-xs whitespace-nowrap"><ProgramCell r={r} /></td>
                <td className="px-3 py-2 ml-mono text-xs whitespace-nowrap" data-testid={`fd-screen-${r.id}`}>
                  {r.display_file ? <>{r.display_file}<span className="text-[var(--ml-text-secondary)]">/{r.record_format || "?"}</span></> : <span className="text-[var(--ml-text-secondary)]/60">—</span>}
                </td>
                <td className="px-3 py-2 ml-mono text-xs whitespace-nowrap"><FileCell r={r} /></td>
                <td className="px-3 py-2"><TypeBadge type={r.reference_type} testid={`fd-type-${r.id}`} /></td>
                <td className="px-3 py-2 text-xs text-[var(--ml-text-secondary)] whitespace-nowrap">{originLabel(r.origin)}</td>
                <td className="px-3 py-2"><ConfidenceBadge confidence={r.confidence} ambiguous={r.ambiguous} testid={`fd-confidence-${r.id}`} /></td>
                <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  {r.origin === "manual" && !r.batch_id ? (
                    <span className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-[var(--ml-border)] text-[var(--ml-text-secondary)]" data-testid="evidence-badge-manual">Manual</span>
                  ) : (
                    <EvidenceBadge batch={r.batch_id ? page.batches?.[r.batch_id] : null} row={{}} workspaceId={workspaceId} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
